import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import {
  ArrowLeft, Trophy, Medal, Search,
  ChevronUp, ChevronDown, ArrowUpDown,
} from 'lucide-react';
import { useRoleData } from '../../hooks/useRoleData';
import { analyticsAPI } from '../../services/api';
import './TraineeLeaderboard.css';

const formatTime = (seconds) => {
  if (!seconds) return '0m';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const TraineeLeaderboard = () => {
  const navigate = useNavigate();
  const { filteredUsers, filteredModules } = useRoleData();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('progress');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    const fetchLeaderboard = async () => {
      setLoading(true);
      try {
        const response = await analyticsAPI.getTraineeLeaderboard();
        setLeaderboard(response.data.data);
      } catch (err) {
        console.error('Failed to fetch leaderboard:', err);
        // Build from local data as fallback
        const trainees = filteredUsers
          .filter(u => u.role === 'Trainee' && u.status === 'Active')
          .map((t, index) => ({
            rank: index + 1,
            id: t.id,
            username: t.username,
            name: t.name,
            email: t.email,
            department: t.department,
            avatar: t.avatar,
            initials: t.initials,
            status: t.status,
            progress: t.progress || 0,
            enrolledModules: t.enrolledModules?.length || 0,
            completedModules: t.completedModules?.length || 0,
            totalTimeSpent: Math.round(Math.random() * 14400),
            joinDate: t.joinDate,
            lastActive: t.lastActive,
          }))
          .sort((a, b) => b.progress - a.progress)
          .map((t, i) => ({ ...t, rank: i + 1 }));
        setLeaderboard(trainees);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, [filteredUsers, filteredModules]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sortedLeaderboard = [...leaderboard]
    .filter(t => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return t.name.toLowerCase().includes(term) ||
        (t.username && t.username.toLowerCase().includes(term)) ||
        t.department.toLowerCase().includes(term);
    })
    .sort((a, b) => {
      const aVal = a[sortField] || 0;
      const bVal = b[sortField] || 0;
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    })
    .map((t, i) => ({ ...t, rank: i + 1 }));

  const topChartData = sortedLeaderboard.slice(0, 10).map(t => ({
    name: t.name.split(' ')[0],
    progress: t.progress,
  }));

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown size={12} className="trainee-lb__sort-icon" />;
    return sortDir === 'asc'
      ? <ChevronUp size={12} className="trainee-lb__sort-icon trainee-lb__sort-icon--active" />
      : <ChevronDown size={12} className="trainee-lb__sort-icon trainee-lb__sort-icon--active" />;
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return <span className="trainee-lb__rank trainee-lb__rank--gold"><Trophy size={14} /></span>;
    if (rank === 2) return <span className="trainee-lb__rank trainee-lb__rank--silver"><Medal size={14} /></span>;
    if (rank === 3) return <span className="trainee-lb__rank trainee-lb__rank--bronze"><Medal size={14} /></span>;
    return <span className="trainee-lb__rank">{rank}</span>;
  };

  return (
    <div className="trainee-lb">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/analytics')}>
            <ArrowLeft size={16} />
            Back to Analytics
          </button>
          <h1>Trainee Leaderboard</h1>
          <p>Compare trainee performance and progress</p>
        </div>
      </div>

      {/* Top Progress Chart */}
      {topChartData.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3>Top Performers</h3>
            <span className="badge badge-dark">{leaderboard.length} trainees</span>
          </div>
          <div className="card-body" style={{ paddingTop: 0 }}>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={topChartData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6b7280' }} />
                <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} domain={[0, 100]} />
                <Tooltip formatter={(v) => [`${v}%`, 'Progress']} />
                <Bar dataKey="progress" name="Progress %" radius={[6, 6, 0, 0]}>
                  {topChartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.progress >= 75 ? '#91cc47' : entry.progress >= 50 ? '#d1dc24' : '#a595c7'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="trainee-lb__toolbar">
        <div className="trainee-lb__search">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search by name, username, or department..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Leaderboard Table */}
      <div className="card">
        <div className="trainee-lb__table">
          <div className="trainee-lb__table-header">
            <span>#</span>
            <span>Trainee</span>
            <span className="trainee-lb__sortable" onClick={() => handleSort('progress')}>
              Progress <SortIcon field="progress" />
            </span>
            <span className="trainee-lb__sortable" onClick={() => handleSort('enrolledModules')}>
              Enrolled <SortIcon field="enrolledModules" />
            </span>
            <span className="trainee-lb__sortable" onClick={() => handleSort('completedModules')}>
              Completed <SortIcon field="completedModules" />
            </span>
            <span className="trainee-lb__sortable" onClick={() => handleSort('totalTimeSpent')}>
              Time Spent <SortIcon field="totalTimeSpent" />
            </span>
            <span>Actions</span>
          </div>
          {loading ? (
            <div className="trainee-lb__loading">Loading...</div>
          ) : sortedLeaderboard.length === 0 ? (
            <div className="trainee-lb__empty">No trainees found</div>
          ) : (
            sortedLeaderboard.map((t) => (
              <div key={t.id} className="trainee-lb__table-row">
                <div>{getRankBadge(t.rank)}</div>
                <div className="trainee-lb__trainee">
                  <div className="trainee-lb__avatar">
                    {t.avatar ? (
                      <img src={t.avatar} alt={t.name} />
                    ) : (
                      <span>{t.initials}</span>
                    )}
                  </div>
                  <div className="trainee-lb__trainee-info">
                    <span className="trainee-lb__trainee-name">{t.name}</span>
                    {t.username && (
                      <span className="trainee-lb__trainee-username">@{t.username}</span>
                    )}
                    <span className="trainee-lb__trainee-dept">{t.department}</span>
                  </div>
                </div>
                <div className="trainee-lb__progress-cell">
                  <div className="progress-bar" style={{ height: 6, flex: 1, maxWidth: 100 }}>
                    <div className="progress-bar-fill" style={{ width: `${t.progress}%` }} />
                  </div>
                  <span className={`analytics__table-pct ${
                    t.progress >= 75 ? 'analytics__table-pct--high' :
                    t.progress >= 50 ? 'analytics__table-pct--mid' : 'analytics__table-pct--low'
                  }`}>
                    {t.progress}%
                  </span>
                </div>
                <span className="trainee-lb__cell">{t.enrolledModules}</span>
                <span className="trainee-lb__cell">{t.completedModules}</span>
                <span className="trainee-lb__cell">{formatTime(t.totalTimeSpent)}</span>
                <div>
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => navigate(`/analytics/trainee/${encodeURIComponent(t.username)}`)}
                    disabled={!t.username}
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default TraineeLeaderboard;
